import { yupResolver } from '@hookform/resolvers/yup';
import { GetEmployerDto } from '@zorro/clients';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Stack } from '@zorro/web/design-system';
import {
  getClassDefinitionYear,
  useBatchCallEndpoint,
  useDynamicRouter,
  useForm,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';

import { EmployeeCustomAttributes } from '../../EmployeeCustomAttributes/EmployeeCustomAttributes';
import { FormFooter } from '../../FormFooter';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';
import {
  ChangeEmployeeCustomClassCategoryFormFields,
  changeEmployeeCustomClassCategorySchema,
} from './changeEmployeeCustomClassCategoryForm.utils';

/**
 * Custom class category is a period-scoped write: every target is an onboarding
 * period, updated through `insuredControllerUpdateCustomClassCategory`. Callers
 * pass the selected records already narrowed to those that have a period (the
 * action is greyed out otherwise), so the form never infers a write target.
 */
type ChangeEmployeeCustomClassCategoryFormProps = {
  recordsForActions: (RecordForActions & { onboardingPeriodId: string })[];
  employerId: GetEmployerDto['id'];
  onClose: () => void;
  onSuccess: () => void;
};

const ChangeEmployeeCustomClassCategoryForm = ({
  recordsForActions,
  employerId,
  onClose,
  onSuccess,
}: ChangeEmployeeCustomClassCategoryFormProps) => {
  const { data: openEnrollmentPeriods = [] } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: [employerId],
  });

  const classDefinitionYear = getClassDefinitionYear(
    openEnrollmentPeriods,
    employerId
  );

  const isSingleTarget = recordsForActions.length === 1;

  const { executeBatchCall, errors } = useBatchCallEndpoint({
    methodName: 'insuredControllerUpdateCustomClassCategory',
    singularItemName: 'onboarding period',
    action: 'updated',
  });

  const {
    control,
    formState: { isValid },
    getValues,
  } = useForm<ChangeEmployeeCustomClassCategoryFormFields>({
    mode: 'all',
    resolver: yupResolver(changeEmployeeCustomClassCategorySchema),
    defaultValues: {
      customAttribute: isSingleTarget
        ? recordsForActions[0].customClassCategory || undefined
        : undefined,
    },
  });

  const { reloadPage } = useDynamicRouter({});

  async function handleSubmit() {
    const { customAttribute } = getValues();

    const batchItems = recordsForActions.map(
      ({ onboardingPeriodId, fullName, employeeId }) => ({
        key: fullName,
        // Group by employee so two periods of the same employee land in
        // different (sequential) batches and never run as concurrent
        // transactions - both update that employee's row and would deadlock.
        batchKey: employeeId,
        params: [
          onboardingPeriodId,
          { customClassCategory: customAttribute },
        ] as const,
      })
    );

    // Skip the batch call's built-in reload so the drawer (and its errors
    // table) stays open on failure; reload once everything settles cleanly.
    const { errors: submitErrors } = await executeBatchCall(batchItems, {
      shouldAvoidPageReload: true,
    });

    if (submitErrors.length === 0) {
      onSuccess();
      reloadPage();
    }
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{ onClick: handleSubmit, disabled: !isValid }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <EmployeeCustomAttributes
        control={control}
        employerId={employerId}
        classDefinitionYears={[classDefinitionYear]}
        name="customAttribute"
        selectProps={{ searchable: true }}
      />

      <ErrorsTable
        tableTitle="Employee name"
        errors={errors}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};

export { ChangeEmployeeCustomClassCategoryForm };
